import { requestJson } from "@/lib/api";
import type { AdminClientDetail, AdminClientSummary, CommercialStatus, ClientType } from "@/lib/types";

interface AdminCustomerListItemDto {
  id: string;
  email: string;
  status: CommercialStatus;
  hasProfile?: boolean;
  companyName?: string | null;
  tradeName?: string | null;
  cnpj?: string | null;
  clientType?: ClientType | null;
  createdAt: string;
  vendorId?: string | null;
  vendorName?: string | null;
  passwordResetRequired?: boolean;
}

interface AdminCustomerProfileDto {
  companyName: string;
  tradeName?: string | null;
  cnpj: string;
  phone: string;
  contactName: string;
  addressCep: string | null;
  addressLogradouro: string | null;
  addressNumero: string | null;
  addressComplemento: string | null;
  addressBairro: string | null;
  addressCidade: string | null;
  addressUf: string | null;
  clientType: ClientType;
}

export interface UpdateAdminCustomerProfileInput {
  email?: string;
  companyName: string;
  tradeName?: string | null;
  cnpj: string;
  phone: string;
  contactName: string;
  addressCep: string;
  addressLogradouro: string;
  addressNumero: string;
  addressComplemento?: string | null;
  addressBairro: string;
  addressCidade: string;
  addressUf: string;
  clientType: ClientType;
}

interface AdminCustomerDto {
  id: string;
  email: string;
  role: "CLIENT" | "ADMIN";
  status: CommercialStatus;
  profile: AdminCustomerProfileDto | null;
  createdAt: string;
  priceTableId?: string | null;
  vendorId?: string | null;
  vendorName?: string | null;
  commissionPercent?: number | null;
  effectiveCommissionPercent?: number | null;
  passwordResetRequired?: boolean;
  smallQuantitySurchargeEnabled?: boolean;
  defaultPaymentTermDays?: number | null;
  defaultPaymentMethod?: "BOLETO" | "PIX" | "TRANSFERENCIA" | "DINHEIRO" | null;
  defaultInstallments?: number;
}

function toAdminClientSummary(customer: AdminCustomerListItemDto): AdminClientSummary {
  return {
    id: customer.id,
    email: customer.email,
    status: customer.status,
    hasProfile: customer.hasProfile ?? false,
    companyName: customer.companyName ?? undefined,
    tradeName: customer.tradeName ?? null,
    cnpj: customer.cnpj ?? null,
    clientType: customer.clientType ?? undefined,
    createdAt: customer.createdAt,
    vendorId: customer.vendorId ?? null,
    vendorName: customer.vendorName ?? null,
    passwordResetRequired: customer.passwordResetRequired ?? false,
  };
}

function toAdminClientDetail(customer: AdminCustomerDto): AdminClientDetail {
  return {
    id: customer.id,
    email: customer.email,
    role: customer.role,
    status: customer.status,
    profile: customer.profile ? { ...customer.profile } : null,
    createdAt: customer.createdAt,
    priceTableId: customer.priceTableId ?? undefined,
    vendorId: customer.vendorId ?? null,
    vendorName: customer.vendorName ?? null,
    commissionPercent: customer.commissionPercent ?? null,
    effectiveCommissionPercent: customer.effectiveCommissionPercent ?? null,
    passwordResetRequired: customer.passwordResetRequired ?? false,
    smallQuantitySurchargeEnabled: customer.smallQuantitySurchargeEnabled ?? true,
    defaultPaymentTermDays: customer.defaultPaymentTermDays ?? null,
    defaultPaymentMethod: customer.defaultPaymentMethod ?? null,
    defaultInstallments: customer.defaultInstallments ?? 1,
  };
}

export function toAdminClientSummaryFromDetail(customer: AdminClientDetail): AdminClientSummary {
  return {
    id: customer.id,
    email: customer.email,
    status: customer.status,
    hasProfile: customer.profile != null,
    companyName: customer.profile?.companyName ?? customer.companyName,
    tradeName: customer.profile?.tradeName ?? null,
    cnpj: customer.profile?.cnpj ?? customer.cnpj ?? null,
    clientType: customer.profile?.clientType ?? customer.clientType,
    createdAt: customer.createdAt,
    passwordResetRequired: customer.passwordResetRequired ?? false,
  };
}

export interface CustomerDeletionGroup {
  key: string;
  label: string;
  count: number;
  items: string[];
}

export interface CustomerDeletionImpact {
  customerId: string;
  label: string;
  status: CommercialStatus;
  deletable: boolean;
  blockReason: string | null;
  groups: CustomerDeletionGroup[];
}

/** Prévia em cascata do que será removido ao excluir o cliente (somente ADMIN). */
export async function fetchCustomerDeletionImpact(customerId: string): Promise<CustomerDeletionImpact> {
  return requestJson<CustomerDeletionImpact>(`/api/v1/admin/customers/${customerId}/deletion-impact`);
}

/** Exclusão definitiva do cliente e de tudo vinculado (somente ADMIN, cliente BLOQUEADO). */
export async function deleteAdminCustomer(customerId: string): Promise<void> {
  await requestJson<void>(`/api/v1/admin/customers/${customerId}`, { method: "DELETE" });
}

export async function fetchAdminCustomers(status?: CommercialStatus): Promise<AdminClientSummary[]> {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  const customers = await requestJson<AdminCustomerListItemDto[]>(`/api/v1/admin/customers${query}`);
  return customers.map(toAdminClientSummary);
}

export async function fetchAdminCustomer(customerId: string): Promise<AdminClientDetail> {
  const customer = await requestJson<AdminCustomerDto>(`/api/v1/admin/customers/${customerId}`);
  return toAdminClientDetail(customer);
}

export async function updateAdminCustomerProfile(
  customerId: string,
  input: UpdateAdminCustomerProfileInput,
): Promise<AdminClientDetail> {
  const customer = await requestJson<AdminCustomerDto>(`/api/v1/admin/customers/${customerId}/profile`, {
    method: "PUT",
    body: input,
  });
  return toAdminClientDetail(customer);
}

export async function updateAdminCustomerStatus(
  customerId: string,
  status: CommercialStatus,
  clientType?: ClientType,
  priceTableId?: string | null,
  vendorId?: string | null,
  commissionPercent?: number | null,
  smallQuantitySurchargeEnabled?: boolean,
): Promise<AdminClientDetail> {
  const body: Record<string, unknown> = { status };
  if (clientType) body.clientType = clientType;
  if (priceTableId !== undefined) body.priceTableId = priceTableId;
  if (vendorId === null) {
    body.clearVendor = true;
  } else if (vendorId !== undefined) {
    body.vendorId = vendorId;
  }
  if (commissionPercent === null) {
    body.clearCommission = true;
  } else if (commissionPercent !== undefined) {
    body.commissionPercent = commissionPercent;
  }
  if (smallQuantitySurchargeEnabled !== undefined) {
    body.smallQuantitySurchargeEnabled = smallQuantitySurchargeEnabled;
  }
  const customer = await requestJson<AdminCustomerDto>(`/api/v1/admin/customers/${customerId}/status`, {
    method: "PATCH",
    body,
  });
  return toAdminClientDetail(customer);
}

/** Senha temporária gerada pelo admin, exibida uma única vez (nunca relistada). */
export interface AdminTemporaryPassword {
  customer: AdminClientDetail;
  temporaryPassword: string;
}

/**
 * Reset de senha pelo admin (cliente esqueceu, sem e-mail): o backend gera uma senha temporária segura,
 * devolvida UMA ÚNICA VEZ, e marca a conta para troca obrigatória. Repassar ao cliente out-of-band.
 */
export async function forcePasswordResetAdminCustomer(customerId: string): Promise<AdminTemporaryPassword> {
  const response = await requestJson<{ customer: AdminCustomerDto; temporaryPassword: string }>(
    `/api/v1/admin/customers/${customerId}/force-password-reset`,
    { method: "POST" },
  );
  return { customer: toAdminClientDetail(response.customer), temporaryPassword: response.temporaryPassword };
}

/** Cancela a marcação de redefinição de senha do cliente. */
export async function cancelPasswordResetAdminCustomer(customerId: string): Promise<AdminClientDetail> {
  const customer = await requestJson<AdminCustomerDto>(
    `/api/v1/admin/customers/${customerId}/force-password-reset`,
    { method: "DELETE" },
  );
  return toAdminClientDetail(customer);
}

/**
 * Condição financeira padrão do cliente (Financeiro). É apenas o padrão do cadastro: ao gerar um título
 * a condição é copiada/congelada nele, e alterar estes campos não afeta títulos já emitidos.
 */
export async function updateAdminCustomerFinancialCondition(
  customerId: string,
  input: {
    defaultPaymentTermDays?: number | null;
    defaultPaymentMethod?: "BOLETO" | "PIX" | "TRANSFERENCIA" | "DINHEIRO" | null;
    defaultInstallments?: number | null;
  },
): Promise<AdminClientDetail> {
  const customer = await requestJson<AdminCustomerDto>(
    `/api/v1/admin/customers/${customerId}/financial-condition`,
    { method: "PATCH", body: input },
  );
  return toAdminClientDetail(customer);
}

